import NoFound from "@/components/shared/NoFound";
import { getPropertyColumns } from "../components/columns";
import CreatePropertyDialog from "../components/CreatePropertyDialog";
import { PropertiesStats } from "../components/PropertiesStats";
import {
  PropertyFilters,
  type PropertyFiltersValues,
} from "../components/PropertyFilters";
import { usePropertiesTableData } from "../hooks/usePropertiesTableData";
import { PropertyResponseDto } from "@/common/api/datasources/Api";
import { usePropertiesQuery } from "@/common/api/services/properties";
import { ErrorBoundary } from "@/common/components/ErrorBoundary";
import { DataTablePagination } from "@/components/shared/DataTable/DataTablePagination";
import { DataTableViewOptions } from "@/components/shared/DataTable/DataTableViewOptions";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { AxiosError } from "axios";
import {
  Building2,
  CameraOff,
  ChevronsDown,
  ChevronsUp,
  Plus,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const PropertiesContent = () => {
  const { t } = useTranslation();
  const columns = getPropertyColumns(t);
  const [isContainerMode, setIsContainerMode] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const {
    page,
    setPage,
    size,
    setSize,
    sorting,
    setSorting,
    columnFilters,
    setColumnFilters,
    columnVisibility,
    setColumnVisibility,
  } = usePropertiesTableData();

  const [filters, setFilters] = useState<PropertyFiltersValues>({
    search: "",
    type: undefined,
    status: undefined,
    minPrice: undefined,
    maxPrice: undefined,
    city: "",
    minArea: undefined,
    maxArea: undefined,
    minRooms: undefined,
    maxRooms: undefined,
    sortBy: undefined,
    sortOrder: undefined,
  });

  const handleFiltersChange = (newFilters: Partial<PropertyFiltersValues>) => {
    setFilters((prev: PropertyFiltersValues) => ({
      ...prev,
      ...newFilters,
    }));
  };

  const propertiesQuery = usePropertiesQuery();

  const {
    data: response,
    isLoading,
    error: propertiesError,
  } = useQuery(
    propertiesQuery.allPropertiesQuery({
      page,
      size,
      ...filters,
    }),
  );

  if (propertiesError) {
    if (
      propertiesError instanceof AxiosError &&
      propertiesError.response?.status === 403
    ) {
      throw new Error(t("common.errors.forbidden"));
    }

    throw propertiesError;
  }

  const properties = response?.content || [];
  const totalElements = response?.totalElements || 0;
  const sizeFromServer = response?.size || size;
  const totalPages = Math.ceil(totalElements / sizeFromServer);

  const table = useReactTable<PropertyResponseDto>({
    data: properties,
    columns,
    pageCount: totalPages,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      pagination: {
        pageIndex: page,
        pageSize: size,
      },
    },
    enableSorting: true,
    enableColumnFilters: true,
    enableRowSelection: false,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === "function"
          ? updater(table.getState().pagination)
          : updater;

      if (newPagination.pageSize !== size) {
        setSize(newPagination.pageSize);
        setPage(0);
        return;
      }

      setPage(newPagination.pageIndex);
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const handleAddProperty = () => {
    setIsCreateDialogOpen(true);
  };

  return (
    <div
      className={cn("space-y-8 p-8", isContainerMode && "container mx-auto")}
    >
      <div className="flex items-center justify-between">
        <PageHeader
          icon={<Building2 />}
          title={t("properties.title")}
          description={t("properties.description")}
          actions={
            <Button onClick={handleAddProperty}>
              <Plus className="mr-2 h-4 w-4" />
              {t("properties.actions.addProperty")}
            </Button>
          }
        />
      </div>

      <CreatePropertyDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
      />

      <PropertiesStats />

      <div className="space-y-4">
        <PropertyFilters
          onFiltersChange={handleFiltersChange}
          defaultValues={filters}
          children={<DataTableViewOptions table={table} />}
        />

        <div className="space-y-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder ? null : (
                          <div
                            className={
                              header.column.getCanSort()
                                ? "cursor-pointer select-none flex items-center space-x-2"
                                : "flex items-center space-x-2"
                            }
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {{
                              asc: <ChevronsUp className="w-4 h-4" />,
                              desc: <ChevronsDown className="w-4 h-4" />,
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {properties.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-96">
                      <NoFound
                        titleTranslationKey="properties.noData.title"
                        descriptionTranslationKey="properties.noData.description"
                        hintTranslationKey="properties.noData.hint"
                        actionTranslationKey="properties.actions.addProperty"
                        onAction={handleAddProperty}
                        icon={Building2}
                        badgeIcon={CameraOff}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} className="hover:bg-muted/50">
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <DataTablePagination
            table={table}
            totalElements={totalElements}
            totalPages={response?.totalPages || 0}
          />
        </div>
      </div>
    </div>
  );
};

const PropertiesPage = () => {
  return (
    <ErrorBoundary>
      <PropertiesContent />
    </ErrorBoundary>
  );
};

export default PropertiesPage;
